import { semantic, type StatusRole } from '@repo/design-tokens';
import type { TagTone } from '@repo/design-system';
import type { CameraWallFeed } from '@repo/ui';
import type { CanvasMarker } from '@repo/ui/planner';

import type { Polygon } from '../../components/PolygonPlotter2/PolygonPlotter2';
import type { AttentionState } from './observerAttention';
import { endpointOfRegion, observerRefid } from './observerSessionState';
import type { ObserverStateByRefid } from './observerSessionState';
import type { FeedScore } from './observerSourceRanking';
import type { ObserverTargetKind } from './observerTypes';

/**
 * The Observers step's stage (`observers-page-stage`): the rules it renders by, kept pure so each
 * is pinned by a test without mounting the 3,000-line step around it.
 */

/** A camera grid: the likely cameras, every camera, or an entity's cameras side by side. */
export type GridView = {
  view: 'suggestions' | 'all-cameras' | 'side-by-side';
  refid: string;
  kind: ObserverTargetKind;
  /** Suggestions opened by *Add Camera*: a pick adds a camera rather than starting a first draw. */
  adding?: boolean;
  /**
   * Suggestions opened to give an entity's observers that sit on NO camera a camera
   * (`observers-page-camera-stage`, FR21): a pick moves them onto it.
   */
  linking?: boolean;
};

/**
 * One camera on the stage (`observers-page-camera-stage`, FR1). `refid: null` is camera by camera:
 * nothing picked, a region drawn first waits for its entity. `from` is where it was opened from, and
 * where Esc and focus go back to.
 */
export type CameraView = {
  view: 'camera';
  endpointId: number;
  refid: string | null;
  kind?: ObserverTargetKind;
  from: StageView;
};

/** What the stage shows. One of these at a time (FR18). */
export type StageView = { view: 'plan' } | GridView | CameraView;

export const PLAN_VIEW: StageView = { view: 'plan' };

/**
 * Where picking an entity lands, by how many camera groups its regions fall into
 * (`observerCameraGroups(...).length` — the group of regions linked to no camera counts, exactly as
 * it counted toward the old pop-up overview). None: the cameras likely to see it. One: straight to
 * that camera. Two or more: side by side.
 */
export const stageViewForPick = (groupCount: number): 'suggestions' | 'camera' | 'side-by-side' =>
  groupCount >= 2 ? 'side-by-side' : groupCount === 1 ? 'camera' : 'suggestions';

/**
 * Esc, one level at a time (FR29): every camera → the likely ones → the plan with the entity still
 * picked → the plan with nothing picked. `clearSelection` is the last step. Every camera steps back
 * to the likely ones only when there ARE likely ones (`hasSuggestions`); otherwise to the plan.
 */
export const stepBack = (
  view: StageView,
  hasSelection: boolean,
  hasSuggestions = true,
): { view: StageView; clearSelection: boolean } | null => {
  if (view.view === 'camera') return { view: view.from, clearSelection: false };
  if (view.view === 'all-cameras' && hasSuggestions)
    return { view: { ...view, view: 'suggestions' }, clearSelection: false };
  if (view.view !== 'plan') return { view: PLAN_VIEW, clearSelection: false };
  return hasSelection ? { view: PLAN_VIEW, clearSelection: true } : null;
};

/**
 * The view the stage SHOWS for the view it was asked for. Decided at render, never frozen at the
 * pick, because the facts it turns on arrive later or change underneath it:
 *
 * - **The likely ones, when none are likely** — nothing placed on the plan to judge by — show every
 *   camera by name instead (FR22). Only once the feeds are KNOWN: while they load, or when their read
 *   failed, the likely-ones grid stays, and says so, rather than deciding "none" from no data.
 * - **Side by side with no camera left** — the editor cleared the entity's last region — shows the
 *   likely ones, as a removal on the stage does (FR27): a pick with no camera never sits side by side.
 */
export const resolveStageView = (
  view: StageView,
  facts: {
    groupCount: number;
    hasSuggestions: boolean;
    feedsKnown: boolean;
    /** The camera view's entity still exists in the plan (`true` when it picks nothing). */
    entityExists?: boolean;
  },
): StageView => {
  if (view.view === 'plan') return view;
  // One camera is shown as asked, unless its entity has gone from the plan: then where it came from.
  if (view.view === 'camera') return facts.entityExists === false ? view.from : view;
  const asked = view.view === 'side-by-side' && facts.groupCount === 0 ? 'suggestions' : view.view;
  const shown =
    asked === 'suggestions' && facts.feedsKnown && !facts.hasSuggestions ? 'all-cameras' : asked;
  return shown === view.view ? view : { ...view, view: shown };
};

/**
 * Each attention state's status role (`onboarding-status-colours-observers`, R1): the one table the
 * plan dot, the Hierarchy dot and the group tone are read from, so they cannot disagree. Giving an
 * entity an observer is this step's job, so a missing one is work to do; a doubtful one is a problem.
 * `done` is measured — by its own observer or a complex measure — and unmarked, so the gaps stand
 * out; `pending` has no look because nothing is known yet.
 */
export const OBSERVER_STATE_ROLE: Readonly<Record<AttentionState, StatusRole | 'done' | null>> = {
  unobserved: 'workToDo',
  'added-unobserved': 'workToDo',
  check: 'problem',
  totalled: 'done',
  configured: 'done',
  pending: null,
};

const roleOf = (state: AttentionState): StatusRole | 'done' | null =>
  OBSERVER_STATE_ROLE[state] ?? null;

const dotOf = (state: AttentionState): string | null => {
  const role = roleOf(state);
  return role && role !== 'done' ? semantic.status.roles[role].mark : null;
};

/**
 * The plan's and the Hierarchy's status dot per attention state, the role's mark. A done or pending
 * entity carries none. A state cast from data that the table does not know draws none either.
 */
export const STATUS_DOT: Readonly<Record<AttentionState, string | null>> = {
  unobserved: dotOf('unobserved'),
  'added-unobserved': dotOf('added-unobserved'),
  check: dotOf('check'),
  totalled: dotOf('totalled'),
  configured: dotOf('configured'),
  pending: dotOf('pending'),
};

/** Each role's model colour by its mark, so a plan dot's colour gives the model's colour. */
const MODEL_BY_MARK: ReadonlyMap<string, string> = new Map(
  Object.values(semantic.status.roles).map(role => [role.mark.toLowerCase(), role.model]),
);

/**
 * Each machine's model colour from the plan's status markers
 * (`spec-onboarding-persistent-scene-status-colour`, R1, R2): a machine's marker is a dot (every
 * space's is an outline), and its colour is its role's `mark`, so the model takes that role's
 * `model`. Observers never shows being edited or just changed, the two roles whose model is not their
 * mark, so every colour here equals its mark. A marker whose colour is no role's gives no colour, and
 * its machine draws white.
 */
export const machineColoursOf = (markers: readonly CanvasMarker[]): ReadonlyMap<string, string> => {
  const colours = new Map<string, string>();
  for (const marker of markers) {
    if (marker.shape === 'outline') continue;
    const model = MODEL_BY_MARK.get(marker.color.toLowerCase());
    if (model) colours.set(marker.id, model);
  }
  return colours;
};

/**
 * The markers as the 3D plans draw them once the models carry the status (R3): every machine's dot
 * marker draws no dot (`statusOnModel`), keeping its ring and its change disc; outlines are unchanged.
 */
export const markersOnModels = (markers: readonly CanvasMarker[]): CanvasMarker[] =>
  markers.map(marker => (marker.shape === 'outline' ? marker : { ...marker, statusOnModel: true }));

/** A group's tone: its role's tag tone, `neutral` for done and for a state the table does not know. */
const toneOf = (state: AttentionState): TagTone => {
  const role = roleOf(state);
  return (role && role !== 'done' ? semantic.status.roles[role].tagTone : null) ?? 'neutral';
};

/**
 * The inventory's groups as the v3 design names them (FR9), toned by their role. The list's group
 * headers, the plan's hover card and the legend all read this one table.
 */
export const GROUP_LOOK: Readonly<
  Record<Exclude<AttentionState, 'pending'>, { title: string; tone: TagTone }>
> = {
  'added-unobserved': { title: 'Added in this draft', tone: toneOf('added-unobserved') },
  check: { title: 'Observer needs checking', tone: toneOf('check') },
  unobserved: { title: 'No observer', tone: toneOf('unobserved') },
  totalled: { title: 'Totalled by a complex measure', tone: toneOf('totalled') },
  configured: { title: 'Has observer', tone: toneOf('configured') },
};

/** The order the groups are listed in (FR9). */
export const GROUP_ORDER: ReadonlyArray<Exclude<AttentionState, 'pending'>> = [
  'added-unobserved',
  'check',
  'unobserved',
  'totalled',
  'configured',
];

/** The caption under a suggested camera: why it sits where it does (FR22). */
export const suggestionCaption = (score: FeedScore, best: boolean): string => {
  const metres = `${Math.round(score.distance)} m`;
  const why =
    score.bucket === 1
      ? `In view on the plan · ${metres}`
      : score.bucket === 2
        ? `Placed, out of view · ${metres}`
        : 'Not on the plan';
  return best ? `Best match · ${why}` : why;
};

/**
 * Says a camera's picture could not be loaded — only when there IS no picture, and the wall has
 * stopped trying. The wall keeps the last frame of a feed whose refresh failed (`error` is a live
 * state, drawn veiled under its pill), so "could not be loaded" under a picture the operator can see
 * is false (backlog item claimed by `observers-page`, FR25). And a feed still `connecting` has no
 * picture YET: loading is not failure, and the tile's own pill already says it is loading.
 */
export const PICTURE_NOT_LOADED = 'Region saved; the picture could not be loaded.';
export const stageTileCaption = (
  feed: Pick<CameraWallFeed, 'imageUrl' | 'state'>,
): string | null => (feed.imageUrl || feed.state === 'connecting' ? null : PICTURE_NOT_LOADED);

/**
 * Take one camera off one entity (FR27): that entity's finished regions on that camera go, with
 * their state entries, and so do the entity's unfinished outlines (an unfinished outline counts as
 * on every camera, so leaving it would seed it onto whichever camera is opened next). No other
 * entity's regions are touched, on this camera or any other. `endpointId: null` removes the group
 * of regions linked to no camera.
 */
export const removeEntityFromCamera = (
  polygonsByEntity: Readonly<Record<string, Polygon[]>>,
  observerState: Readonly<ObserverStateByRefid>,
  refid: string,
  endpointId: number | null,
): {
  polygonsByEntity: Record<string, Polygon[]>;
  observerState: ObserverStateByRefid;
} => {
  const nextState: ObserverStateByRefid = { ...observerState };
  const next: Record<string, Polygon[]> = {};
  for (const [entity, polygons] of Object.entries(polygonsByEntity)) {
    const kept: Polygon[] = [];
    for (const polygon of polygons) {
      const goes =
        entity === refid &&
        (!polygon.isComplete || endpointOfRegion(entity, polygon, observerState) === endpointId);
      if (!goes) {
        kept.push(polygon);
        continue;
      }
      // Undo restores the session as it stood before (FR16), so nothing is kept here for it.
      if (polygon.isComplete) delete nextState[observerRefid(entity, polygon.id)];
    }
    next[entity] = kept.length === polygons.length ? polygons : kept;
  }
  return { polygonsByEntity: next, observerState: nextState };
};

/**
 * Esc on the camera view, one level per press (`observers-page-camera-stage`, FR18). The drawing
 * area answers an OPEN region itself (it cancels it and stops the key), so this starts one level up:
 * *Add Observer* armed (`observer-unified-draw-edit`, R4.3), the shortcut card, a waiting region,
 * the selected observer, the pick (camera by camera only — an entity's camera has no "nothing
 * picked" state of its own), then back to where the camera was opened from.
 */
export type CameraEscape =
  | 'disarm'
  | 'close-shortcuts'
  | 'discard-waiting'
  | 'deselect-observer'
  | 'clear-pick'
  | 'leave';

export const cameraStepBack = (facts: {
  /** *Add Observer* has armed a region (R4.3): the first level, and only while armed. */
  armed?: boolean;
  shortcutsOpen: boolean;
  waiting: boolean;
  selectedObserver: boolean;
  /** The camera was opened with nothing picked (the Cameras view), and an entity is picked now. */
  pickedInCameraByCamera: boolean;
}): CameraEscape => {
  if (facts.armed) return 'disarm';
  if (facts.shortcutsOpen) return 'close-shortcuts';
  if (facts.waiting) return 'discard-waiting';
  if (facts.selectedObserver) return 'deselect-observer';
  if (facts.pickedInCameraByCamera) return 'clear-pick';
  return 'leave';
};

/**
 * Does the camera on stage stay when entity `refid` is picked (`observers-page-camera-stage`, FR14)?
 * Yes when the entity already has a complete region on it, when the camera's cone on the plan
 * contains it (`feedSight`'s `inView`; `null` when the camera is not placed), or when the pick came
 * from this camera's own lists in the Cameras view. Otherwise the stage goes where picking it from the
 * plan would (`stageViewForPick`).
 */
export const cameraSticks = (facts: {
  refid: string;
  endpointId: number;
  polygonsByEntity: Record<string, Polygon[]>;
  observerState: ObserverStateByRefid;
  inView: ReadonlySet<string> | null;
  fromCameraList: boolean;
}): boolean => {
  if (facts.fromCameraList) return true;
  const own = facts.polygonsByEntity[facts.refid] ?? [];
  if (own.some(p => endpointOfRegion(facts.refid, p, facts.observerState) === facts.endpointId))
    return true;
  return !!facts.inView && facts.inView.has(facts.refid);
};

/** How many feeds the likely-cameras grid shows before *All Cameras* takes over. */
export const SHORTLIST_SIZE = 4;

/*
 * The two sentences about a feed READ, written once (moved from the deleted camera picker).
 *
 * They are customer-facing copy about a customer's gym, rendered on the stage's grids and in the
 * navigator's Cameras view, and "we could not find out" and "there are none" must never read as each
 * other.
 */
/** The feed read still on its way — the third of the read's honest outcomes, one wording. */
export const FEEDS_LOADING = 'Loading this twin\u2019s camera feeds\u2026';
export const FEEDS_UNREADABLE_TITLE = "This twin's camera feeds could not be read.";
export const FEEDS_UNREADABLE_BODY =
  'The notice at the top of this step can retry it. Until it succeeds there is nothing to draw on — this is not the same as the twin having no cameras.';
export const NO_FEEDS_BODY =
  "This twin has no camera feeds. Feeds are added on the Camera Feeds step, or by the site's own IT setup.";
